import React, { useState } from 'react'
import {
  Config,
  countAffectedRows,
  CrateAction,
  saveActions,
  stagedIfChanged,
  withoutUndefinedActions,
} from 'domain/Assortment'
import { Dialog } from '../../Dialog'
import { StagedModifications } from '../StagedModifications'
import { CrateActionDialog, CrateActionSnapshot, DialogState, DisabledCrateActions } from './CrateActionDialog'
import { EditableActions, FinalStatus, StagedAction } from '../CommonComponents'
import { CrateFile, CrateGroup, Modification } from 'domain/DTO'
import { SortableHeader, useTableSorting } from '../TableColumnSort'
import { paginationOptions } from '../AssortmentDatabase'
import { getPageData, PagedTableControls } from '@tomra/react-table'
import { logError } from '../../../lib/logError'
import { hasWriteCommonAssortmentPermission } from '../../../lib/Permission'
import {
  AcceptedValue,
  InAssortmentValue,
  MinimumCrateItemsValue,
  RefundGroupValue,
  SuperGroupValue,
} from './CrateActionElements'
import { EditIcon } from '../../../icons'
import { ModifyAllDialog } from './CrateAllActionDialog'
import {
  acceptActionId,
  allAcceptOrRejectActionId,
  allMinimumCrateItemsActionId,
  calculateFinalStatus,
  createCrateActions,
  inAssortmentActionId,
  minimumCrateItemsActionId,
  refundGroupActionId,
  superGroupActionId,
} from './CrateTableUtils'

type Props = {
  config: Config
  database: CrateFile
  commonModifications: Modification[]
  modificationUrl: string
  refreshModifications(): void
}

export const CrateCommonTable = ({
  config,
  database,
  commonModifications,
  modificationUrl,
  refreshModifications,
}: Props) => {
  const [dialogState, setDialogState] = useState({ show: false } as DialogState)
  const [dialogAllState, setDialogAllState] = useState({ show: false } as DialogState)
  const [currentPage, setCurrentPage] = useState(1)
  const [recordsPrPage, setRecordsPrPage] = useState(100)
  const [filterGroup, setFilterGroup] = useState('')
  const [filterName, setFilterName] = useState('')
  const [staged, setStaged] = useState<{ [key: string]: CrateAction | undefined }>({})
  const common = createCrateActions(commonModifications)

  const sorting = useTableSorting<CrateGroup>(
    {
      'Crate Group': (cg) => cg.id,
      Name: (cg) => cg.name,
      CommonConfig: (cg) => {
        const id = cg.id
        return (
          [acceptActionId(id), inAssortmentActionId(id), refundGroupActionId(id), minimumCrateItemsActionId(id)]
            .map((id) => common[id] ?? staged[id])
            .filter((action) => !!action)[0]
            ?.type.toString() ?? ''
        )
      },
      Override: (cg) => {
        const id = cg.id
        return (
          [acceptActionId(id), inAssortmentActionId(id), refundGroupActionId(id), minimumCrateItemsActionId(id)]
            .map((id) => staged[id])
            .filter((action) => !!action)[0]
            ?.type.toString() ?? ''
        )
      },
      Accepted: (cg) => {
        return calculateFinalStatus(cg, common, {}, staged).toString()
      },
    },
    'Crate Group',
  )

  const rows = database?.crateGroups
    ? sorting.sort(
        Array.from(database.crateGroups)
          .filter((cg) => `crate ${cg.id}`.toLocaleLowerCase().includes(filterGroup.toLocaleLowerCase()))
          .filter((cg) => `${cg.name}`.toLocaleLowerCase().includes(filterName.toLocaleLowerCase())),
      )
    : []

  function handleModifyAllOnClick() {
    return () =>
      setDialogAllState({
        id: 'modifyAll',
        show: true,
        snapshot: {
          allMinimumCrateItemsAction: staged[allMinimumCrateItemsActionId()] ?? common[allMinimumCrateItemsActionId()],
          allAcceptOrRejectAction: staged[allAcceptOrRejectActionId()] ?? common[allAcceptOrRejectActionId()],
        },
        disabled: {} as DisabledCrateActions,
      })
  }

  return (
    <div>
      <div className="my-md pl-lg">
        {hasWriteCommonAssortmentPermission() && (
          <StagedModifications numberOfStaged={getNumberOfStaged()} onClear={resetStaged} onApply={applyStaged} />
        )}
      </div>
      <div className="flex items-center pl-lg py-sm">
        <div className="inline-block items-center">
          <label>
            <span>Crate Group:</span>
            <input
              type="text"
              className="mx-1"
              style={{ width: '60%' }} // overrides input[type="text"] {width: 100%}
              data-testid="search-bottle-group"
              onChange={(e) => {
                setFilterGroup(e.target.value.toLocaleLowerCase())
                setCurrentPage(1)
              }}
            />
          </label>
          <span
            className="font-bold cursor-pointer"
            title={'You can search for crate groups explicitly like: `crate 1010`'}
          >
            ?
          </span>
        </div>
        <div className="inline-block items-center ml-1">
          <label>
            <span>Name:</span>
            <input
              type="text"
              className="mx-1"
              style={{ width: '60%' }} // overrides input[type="text"] {width: 100%}
              data-testid="search-name"
              onChange={(e) => {
                setFilterName(e.target.value.toLocaleLowerCase())
                setCurrentPage(1)
              }}
            />
          </label>
        </div>
        {hasWriteCommonAssortmentPermission() && (
          <div className="flex flex-1 justify-end mr-5">
            <button className="btn mr-4" onClick={handleModifyAllOnClick()}>
              <span className="flex items-center justify-between w-full">
                <span className="mr-1">Modify all</span>
                <EditIcon color="none" />
              </span>
            </button>
          </div>
        )}
      </div>

      <table className="table table-fixed mt-1">
        <thead>
          <tr key={'header'}>
            <SortableHeader name="Crate Group" currentSort={sorting.currentSort} />
            <SortableHeader name="Name" currentSort={sorting.currentSort} />
            <th>Database</th>
            <th>Modification All</th>
            <SortableHeader name="CommonConfig" currentSort={sorting.currentSort} />
            <SortableHeader name="Accepted" currentSort={sorting.currentSort} />
          </tr>
        </thead>
        <tbody>
          {getPageData(rows, currentPage, recordsPrPage).map((crateGroup) => {
            const id = crateGroup.id.toString()
            return (
              <tr key={id} className="even:bg-grey-light hover:bg-fog">
                <td>{id}</td>
                <td>{crateGroup.name}</td>
                <td>
                  <AcceptedValue accepted={crateGroup.accepted} />
                  <InAssortmentValue inAssortment={crateGroup.inAssortment} />
                  <RefundGroupValue refund={crateGroup.refundGroup} />
                  <MinimumCrateItemsValue minimumCrateItems={crateGroup.minimumCrateItems} />
                  <SuperGroupValue superGroup={crateGroup.superGroup} />
                </td>
                <td>
                  <StagedAction
                    original={common[allMinimumCrateItemsActionId()]}
                    staged={staged[allMinimumCrateItemsActionId()]}
                  />
                  <StagedAction
                    original={common[allAcceptOrRejectActionId()]}
                    staged={staged[allAcceptOrRejectActionId()]}
                  />
                </td>
                <td>
                  {hasWriteCommonAssortmentPermission() && (
                    <EditableActions onClick={() => openDialog(id)}>
                      {[
                        acceptActionId(id),
                        inAssortmentActionId(id),
                        refundGroupActionId(id),
                        minimumCrateItemsActionId(id),
                        superGroupActionId(id),
                      ]
                        .filter((id) => common[id] || staged[id])
                        .map((id) => (
                          <StagedAction key={id} original={common[id]} staged={staged[id]} />
                        ))}
                    </EditableActions>
                  )}
                </td>
                <td>
                  <FinalStatus ok={calculateFinalStatus(crateGroup, common, {}, staged)} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <PagedTableControls
        currentPage={currentPage}
        numOfItems={rows.length}
        recordsPerPage={recordsPrPage}
        onPageUpdate={setCurrentPage}
        recordsPerPageOptions={paginationOptions}
        onRecordsPerPageChange={(recordsPerPage: number) => {
          setRecordsPrPage(recordsPerPage)
          setCurrentPage(1)
        }}
      />
      {dialogState.show && dialogState.id ? (
        <Dialog onClose={() => setDialogState({ ...dialogState, show: false })} shouldCloseOnOverlayClick={false}>
          <CrateActionDialog
            config={config}
            id={dialogState.id}
            snapshot={dialogState.snapshot}
            disabled={dialogState.disabled}
            onApply={(id, snapshot) => {
              onUserEditedActions(id, snapshot)
              setDialogState({ ...dialogState, show: false })
            }}
          />
        </Dialog>
      ) : null}
      {dialogAllState.show && (
        <Dialog onClose={() => setDialogAllState({ ...dialogAllState, show: false })} shouldCloseOnOverlayClick={false}>
          <ModifyAllDialog
            config={config}
            id={'all'}
            snapshot={{
              allMinimumCrateItemsAction:
                staged[allMinimumCrateItemsActionId()] ?? common[allMinimumCrateItemsActionId()],
              allAcceptOrRejectAction: staged[allAcceptOrRejectActionId()] ?? common[allAcceptOrRejectActionId()],
            }}
            disabled={dialogAllState.disabled}
            onApply={(id: string, snapshot: CrateActionSnapshot) => {
              onUserEditedActions(id, snapshot)
              setDialogAllState({ ...dialogAllState, show: false })
            }}
          />
        </Dialog>
      )}
    </div>
  )

  function getNumberOfStaged() {
    return staged[allMinimumCrateItemsActionId()] || staged[allAcceptOrRejectActionId()]
      ? rows.length
      : countAffectedRows(Object.keys(withoutUndefinedActions(staged)))
  }

  function resetStaged() {
    setStaged({})
  }

  async function applyStaged() {
    try {
      await saveActions('CRATE', modificationUrl, common, staged)
      setStaged({})
      refreshModifications()
    } catch (error: unknown) {
      logError(error)
    }
  }

  function onUserEditedActions(id: string, snapshot: CrateActionSnapshot) {
    setStaged({
      ...staged,
      [acceptActionId(id)]: stagedIfChanged(snapshot.acceptAction, common[acceptActionId(id)]),
      [inAssortmentActionId(id)]: stagedIfChanged(snapshot.inAssortmentAction, common[inAssortmentActionId(id)]),
      [refundGroupActionId(id)]: stagedIfChanged(snapshot.refundGroupAction, common[refundGroupActionId(id)]),
      [minimumCrateItemsActionId(id)]: stagedIfChanged(
        snapshot.minimumCrateItemsAction,
        common[minimumCrateItemsActionId(id)],
      ),
      [allMinimumCrateItemsActionId()]: stagedIfChanged(
        snapshot.allMinimumCrateItemsAction,
        common[allMinimumCrateItemsActionId()],
      ),
      [allAcceptOrRejectActionId()]: stagedIfChanged(
        snapshot.allAcceptOrRejectAction,
        common[allAcceptOrRejectActionId()],
      ),
      [superGroupActionId(id)]: stagedIfChanged(snapshot.superGroupAction, common[superGroupActionId(id)]),
    })
  }

  function openDialog(id: string) {
    setDialogState({
      show: true,
      id: id,
      snapshot: {
        acceptAction: staged[acceptActionId(id)] ?? common[acceptActionId(id)],
        inAssortmentAction: staged[inAssortmentActionId(id)] ?? common[inAssortmentActionId(id)],
        refundGroupAction: staged[refundGroupActionId(id)] ?? common[refundGroupActionId(id)],
        allMinimumCrateItemsAction: staged[allMinimumCrateItemsActionId()] ?? common[allMinimumCrateItemsActionId()],
        minimumCrateItemsAction: staged[minimumCrateItemsActionId(id)] ?? common[minimumCrateItemsActionId(id)],
        superGroupAction: staged[superGroupActionId(id)] ?? common[superGroupActionId(id)],
      },
      disabled: {
        acceptAction:
          typeof common[acceptActionId(id)]?.allowOverride !== 'undefined' &&
          !common[acceptActionId(id)]?.allowOverride,
        inAssortmentAction:
          typeof common[inAssortmentActionId(id)]?.allowOverride !== 'undefined' &&
          !common[inAssortmentActionId(id)]?.allowOverride,
        refundGroupAction:
          typeof common[refundGroupActionId(id)]?.allowOverride !== 'undefined' &&
          !common[refundGroupActionId(id)]?.allowOverride,
        minimumCrateItemsAction:
          typeof common[minimumCrateItemsActionId(id)]?.allowOverride !== 'undefined' &&
          !common[minimumCrateItemsActionId(id)]?.allowOverride,
        increaseBottleToleranceAction: false,
        allMinimumCrateItemsAction:
          typeof common[allMinimumCrateItemsActionId()]?.allowOverride !== 'undefined' &&
          !common[allMinimumCrateItemsActionId()]?.allowOverride,
        allAcceptOrRejectAction:
          typeof common[allAcceptOrRejectActionId()]?.allowOverride !== 'undefined' &&
          !common[allAcceptOrRejectActionId()]?.allowOverride,
        superGroupAction:
          typeof common[superGroupActionId(id)]?.allowOverride !== 'undefined' &&
          !common[superGroupActionId(id)]?.allowOverride,
      },
    })
  }
}
